import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { organizationAPI } from '../services/api';
import { Organization } from '../types';

const OrganizationManagement: React.FC = () => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState<Organization | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    domain: '',
    industry: '',
    size: '',
  });

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const response = await organizationAPI.getOrganizations();
      setOrganizations(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setDialogMode('create');
    setFormData({
      name: '',
      description: '',
      domain: '',
      industry: '',
      size: '',
    });
    setOpenDialog(true);
  };

  const handleOpenEdit = (org: Organization) => {
    setDialogMode('edit');
    setSelectedOrg(org);
    setFormData({
      name: org.name,
      description: org.description || '',
      domain: org.domain || '',
      industry: org.industry || '',
      size: org.size || '',
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedOrg(null);
    setError('');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      // normalize payload
      const payload = {
        name: (formData.name || '').trim(),
        description: formData.description || '',
        domain: (formData.domain || '').trim().toLowerCase(),
        industry: formData.industry || '',
        // send undefined for empty size to avoid enum validation problems
        size: formData.size ? formData.size : undefined,
      } as any;
      if (!payload.name) {
        setError('Organization name is required');
        return;
      }
      if (dialogMode === 'create') {
        await organizationAPI.createOrganization(payload);
        setSuccess('Organization created successfully!');
      } else if (selectedOrg) {
        await organizationAPI.updateOrganization(selectedOrg._id, payload);
        setSuccess('Organization updated successfully!');
      }
      
      handleCloseDialog();
      fetchOrganizations();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      const backendMsg = err.response?.data?.message;
      if (backendMsg) setError(backendMsg);
      else setError(`Failed to ${dialogMode} organization`);
    }
  };

  const handleDeleteClick = (org: Organization) => {
    setOrgToDelete(org);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!orgToDelete) return;

    try {
      await organizationAPI.deleteOrganization(orgToDelete._id);
      setSuccess('Organization deleted successfully!');
      setDeleteConfirmOpen(false);
      setOrgToDelete(null);
      fetchOrganizations();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete organization');
      setDeleteConfirmOpen(false);
    }
  };

  const handleViewUsers = (orgId: string) => {
    navigate(`/organizations/${orgId}/users`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/dashboard')}
        sx={{ mb: 2 }}
      >
        Back to Dashboard
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Organization Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
        >
          Add Organization
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Domain</TableCell>
              <TableCell>Industry</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Users</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {organizations.map((org) => (
              <TableRow key={org._id}>
                <TableCell>
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      {org.name}
                    </Typography>
                    {org.description && (
                      <Typography variant="caption" color="text.secondary">
                        {org.description}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>{org.domain || '-'}</TableCell>
                <TableCell>{org.industry || '-'}</TableCell>
                <TableCell>{org.size || '-'}</TableCell>
                <TableCell>
                  <Chip 
                    label={org.isActive ? 'Active' : 'Inactive'} 
                    color={org.isActive ? 'success' : 'default'} 
                    size="small" 
                  />
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    startIcon={<PeopleIcon />}
                    onClick={() => handleViewUsers(org._id)}
                  >
                    {org.userCount || 0}
                  </Button>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenEdit(org)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteClick(org)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Add New Organization' : 'Edit Organization'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Organization Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={2}
              />
              <TextField
                fullWidth
                label="Domain (e.g., company.com)"
                name="domain"
                value={formData.domain}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                label="Industry"
                name="industry"
                value={formData.industry}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                select
                label="Company Size"
                name="size"
                value={formData.size}
                onChange={handleChange}
              >
                <MenuItem value="">Select size</MenuItem>
                <MenuItem value="1-10">1-10 employees</MenuItem>
                <MenuItem value="11-50">11-50 employees</MenuItem>
                <MenuItem value="51-200">51-200 employees</MenuItem>
                <MenuItem value="201-500">201-500 employees</MenuItem>
                <MenuItem value="501-1000">501-1000 employees</MenuItem>
                <MenuItem value="1000+">1000+ employees</MenuItem>
              </TextField>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {dialogMode === 'create' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete organization <strong>{orgToDelete?.name}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OrganizationManagement;
